import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Robot } from "@phosphor-icons/react";

const CARDS = [
  ["total_calls", "SI calls handled"],
  ["routed", "Successful routes"],
  ["confirmations", "Confirmations"],
  ["clarifications", "Clarifications"],
  ["unrecognized", "Unrecognized"],
  ["transfers_to_human", "To operator"],
  ["voicemail", "Voicemail fallback"],
  ["not_found", "Bad extension"],
];

export default function PbxAiUsage({ orgId }) {
  const { data, isLoading } = useQuery({
    queryKey: ["pbx-ai-usage", orgId],
    queryFn: async () => (await api.get("/pbx/ai-usage", { params: orgId ? { org_id: orgId } : {} })).data,
  });
  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const m = data?.metrics || {};
  const byIntent = data?.by_intent || {};
  const recent = data?.recent || [];

  return (
    <div className="space-y-6" data-testid="pbx-ai-usage">
      <p className="text-sm text-muted-foreground">Per-tenant SI Receptionist activity (billable). Use this to price and improve Business PBX plans.</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {CARDS.map(([k, label]) => (
          <div key={k} data-testid={`usage-${k}`} className="rounded-2xl border border-white/10 bg-card p-4">
            <p className="font-mono-metric text-2xl font-semibold">{m[k] ?? 0}</p>
            <p className="mt-1 text-xs text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>

      {Object.keys(byIntent).length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Routes by intent</h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.entries(byIntent).map(([name, n]) => (
              <span key={name} className="rounded-full bg-primary/15 px-3 py-1 text-xs font-medium text-primary">{name} · {n}</span>
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recent calls</h3>
        {recent.length === 0 ? (
          <div className="mt-2 rounded-2xl border border-dashed border-white/15 bg-card p-8 text-center">
            <Robot size={24} className="mx-auto text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">No SI receptionist calls yet.</p>
          </div>
        ) : (
          <div className="mt-2 overflow-hidden rounded-xl border border-white/10 bg-card">
            {recent.slice(0, 40).map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 border-b border-white/5 px-4 py-2.5 text-xs last:border-0">
                <span className="col-span-2 font-mono-metric text-muted-foreground">{(r.at || "").slice(11, 19)}</span>
                <span className="col-span-6 truncate">{r.transcript || "—"}</span>
                <span className={`col-span-2 font-semibold ${r.action === "route" ? "text-emerald-500" : r.action === "unrecognized" ? "text-amber-500" : "text-muted-foreground"}`}>{r.action}{r.routed_ext ? ` ${r.routed_ext}` : ""}</span>
                <span className="col-span-2 text-right text-muted-foreground">{r.intent || r.method || ""}{r.confidence != null ? ` ${Math.round(r.confidence * 100)}%` : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
